import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Skeleton } from '@mui/material';

import { UppyUploaderProps } from '../../interfaces/uppy';

import DocumentItem from './DocumentItem/DocumentItem';
import UppyDashboard from './UppyDashboard';
import useUppy from './useUppy';

const locale = {
    strings: {
        dropPasteFiles: '%{browseFiles} or drag and drop',
        browseFiles: 'Upload a file',
        addMore: 'Add another',
        cancel: 'Remove all',
        xFilesSelected: {
            '0': '%{smart_count} files added',
            '1': '%{smart_count} files added',
        },
    },
};

const UppyUploader = forwardRef(
    (
        {
            documentType,
            meta = {},
            options = {},
            xhrOptions = {},
            shouldPreloadData = true,
            hideUploadedDocuments = false,
            filenameReplace,
            memberUUID,
            trigger,
            height,
            onUploadSuccess,
            onUploadError,
            onDataLoad,
            onFileAdded,
            onFileRemoved,
            disabled,
            filterByMetaFields = false,
            ...props
        }: UppyUploaderProps,
        ref
    ) => {
        const { uppy, isLoading, documents, restrictions } = useUppy({
            documentType,
            meta,
            options,
            xhrOptions,
            filenameReplace,
            memberUUID,
            shouldPreloadData,
            onUploadSuccess,
            onUploadError,
            onDataLoad,
            onFileAdded,
            onFileRemoved,
            filterByMetaFields,
        });

        useImperativeHandle(ref, () => uppy, [uppy]);

        const [isTriggered, setIsTriggered] = useState(false);

        useEffect(() => {
            if (!trigger || isLoading || uppy.getFiles().length === 0) {
                return;
            }
            if (!isTriggered) {
                uppy.upload();
                setIsTriggered(true);
            } else {
                uppy.retryAll();
            }
        }, [trigger]);

        const hasDocumentsToShow = documents && documents.length < (restrictions.maxNumberOfFiles ?? Infinity);

        if (isLoading) {
            return (
                <Skeleton
                    variant="rectangular"
                    animation="wave"
                    sx={{ bgcolor: 'grey.50', borderRadius: '8px', m: 0, height }}
                />
            );
        }

        return (
            <>
                {documents &&
                    !hideUploadedDocuments &&
                    documents.map((document) => (
                        <DocumentItem
                            key={`${document.file_name}_${document.id ?? document.uuid}`}
                            document={document}
                            disabled={disabled}
                        />
                    ))}

                {(!shouldPreloadData || hasDocumentsToShow) && (
                    <UppyDashboard
                        uppy={uppy}
                        height={height ?? 'auto'}
                        width="100%"
                        locale={locale}
                        proudlyDisplayPoweredByUppy={false}
                        disabled={disabled}
                        {...props}
                    />
                )}
            </>
        );
    }
);

export default UppyUploader;
